<template>
    <div data-test-role="stripeCheckoutDialog">
        <pc-input
            v-model.trim="internalName"
            label="Name"
            name="name"
            v-bind:validator="nameValidator"
            class="mb-2"
            required
            v-on:validated="onValidated('name', $event)" />
        <pc-input
            v-model.trim="internalEmail"
            label="Email"
            name="email"
            class="mb-2"
            data-test-role="stripeElementsEmail"
            v-bind:validator="emailValidator"
            required
            v-on:validated="onValidated('email', $event)" />
        <div id="stripe-payment-element" />
        <div
            v-bind:class="{ 'd-block': stripeErrorMessage }"
            class="invalid-feedback fw-bold">
            {{ stripeErrorMessage }}
        </div>
        <hr />
        <div>
            <div class="my-2 row gx-3">
                <div class="col-4 d-flex">
                    <pc-loader
                        small
                        v-bind:loading="paymentIsProcessing">
                        <pc-button
                            style="min-width: 0"
                            v-bind:disabled="paymentIsProcessing"
                            v-on:click="cancel">
                            Cancel
                        </pc-button>
                    </pc-loader>
                </div>
                <div class="col-4 d-flex justify-content-center">
                    <pc-loader
                        small
                        v-bind:loading="paymentIsProcessing">
                        <pc-button
                            primary
                            v-bind:disabled="!canPay"
                            data-test-role="stripeElementsPayBtn"
                            v-on:click="getPaymentMethodToken">
                            Pay {{ displayAmount }}
                        </pc-button>
                    </pc-loader>
                </div>
            </div>
        </div>
        <div
            v-bind:class="{ 'd-block': stripeTokenErrorMessage }"
            class="invalid-feedback fw-bold">
            {{ stripeTokenErrorMessage }}
        </div>
    </div>
</template>

<script>
    import { getValidator, VALIDATOR_TYPES } from "Validation/validator";

    export default {
        props: {
            name: { type: String, required: false, default: null },
            email: { type: String, required: false, default: null },
            token: { type: String, required: true },
            stripe: { type: Object, required: true },
            displayAmount: { type: String, required: true },
            chargeId: { type: String, required: true },
            availableConfirmPaymentRedirectValues: { type: Array, required: true }
        },
        data() {
            return {
                internalName: this.name,
                internalEmail: this.email,
                nameValidator: getValidator(this.$app, VALIDATOR_TYPES.required),
                emailValidator: getValidator(this.$app, [VALIDATOR_TYPES.required, VALIDATOR_TYPES.email]),
                paymentIsProcessing: false,
                validationResults: {
                    name: null,
                    email: null
                },
                stripeResult: null,
                stripeTokenErrorMessage: null
            };
        },
        computed: {
            hasErrors() {
                if (this.validationResults.name && this.validationResults.name.hasError) {
                    return true;
                }
                if (this.validationResults.email && this.validationResults.email.hasError) {
                    return true;
                }
                if (this.stripeErrorMessage) {
                    return true;
                }
                return false;
            },
            canPay() {
                if (!this.stripeResult) {
                    return false;
                }

                return !this.hasErrors && Boolean(this.stripeResult.complete);
            },
            stripeErrorMessage() {
                if (!this.stripeResult || !this.stripeResult.error || !this.stripeResult.error.message) {
                    return null;
                }
                return this.stripeResult.error.message;
            }
        },
        async mounted() {
            await this.$nextTick();

            this.elements = this.stripe.elements({
                clientSecret: this.token,
                fonts: [
                    {
                        cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans"
                    }
                ],
                appearance: {
                    variables: {
                        fontFamily: "Open Sans, sans-serif",
                        fontSizeBase: "13px"
                    }
                }
            });

            this.payment = this.elements.create("payment", {
                fields: {
                    billingDetails: {
                        name: "never",
                        email: "never"
                    }
                }
            });
            this.payment.mount("#stripe-payment-element");
            this.payment.on("change", (event) => {
                this.stripeResult = event || null;
            });
        },
        methods: {
            cancel() {
                if (this.paymentIsProcessing) {
                    return;
                }
                this.$app.closeDialog({
                    resolve: true,
                    resolveValue: { success: false }
                });
            },
            onValidated(prop, validationResult) {
                this.validationResults[prop] = validationResult;
            },
            async getPaymentMethodToken() {
                if (this.paymentIsProcessing) {
                    return;
                }
                this.paymentIsProcessing = true;
                await this.$nextTick(); // to allow loader to appear
                //we need to remove the unsaved changes so that if the redirect occures it continues.
                this.$app.setUnsavedChanges(false);

                const url = new URL(window.location.href);
                const useAlwaysRedirect =
                    url.searchParams.get("pc_stripe_test_redirect") === "always" &&
                    this.availableConfirmPaymentRedirectValues.includes("always");

                const { paymentIntent, error } = await this.stripe.confirmPayment({
                    elements: this.elements,
                    confirmParams: {
                        receipt_email: this.internalEmail,
                        // redirect based payment methods won't work with versaforms as it stands. currently though, we only support card and ach, both of which can work without the redirect.
                        return_url:
                            location.href +
                            "&pc_from_stripe=" +
                            (this.$app?.LoggedInUser?.PublicId ?? "wont_work") +
                            "&pc_pcid=" +
                            this.chargeId,
                        payment_method_data: {
                            billing_details: {
                                name: this.internalName,
                                email: this.internalEmail
                            }
                        }
                    },
                    redirect: useAlwaysRedirect ? "always" : "if_required"
                });

                if (error || !paymentIntent || !paymentIntent.id) {
                    this.stripeTokenErrorMessage = "Something went wrong. Please try again.";
                }
                if (error) {
                    if (error.type === "card_error" || error.type === "validation_error") {
                        this.stripeTokenErrorMessage = error.message;
                    }
                    if (error["payment_intent"] && error["payment_intent"].status === "canceled") {
                        // eslint-disable-next-line max-len
                        this.stripeTokenErrorMessage =
                            "The payment session on this page has been cancelled due to a simultaneous payment attempt for this project. Please refresh the page.";
                    }
                }

                if (paymentIntent && paymentIntent.id) {
                    this.$app.closeDialog({
                        resolve: true,
                        resolveValue: { success: true, token: paymentIntent.id }
                    });
                }
                this.paymentIsProcessing = false;
            }
        }
    };
</script>
